import { useEffect, useRef, useState } from "react";
import type { LinkRecenzjiAdmin } from "@shared/recenzjeTypy.js";
import type { StanZasobu } from "../../hooks/useZasob.js";
import { api, ApiError } from "../../lib/api.js";
import { czyFokusZginal, najblizszyPrzycisk } from "../../lib/fokus.js";
import {
  ETYKIETY_STATUSU_LINKU,
  WARIANTY_STATUSU_LINKU,
  komunikatBleduRecenzji,
  metaLinku,
  pelnyAdresLinku,
  postepLinku,
} from "../../lib/recenzje.js";
import { IconKopiuj } from "../icons/index.js";
import { Avatar } from "../ui/Avatar.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { ConfirmDialog } from "../ui/ConfirmDialog.js";
import { ProgressBar } from "../ui/ProgressBar.js";
import { useToast } from "../ui/Toast.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";
import { useKopiowanie } from "./useKopiowanie.js";

/**
 * Karta "Linki" w panelu Recenzje: kto dostal link, ile rozmow, kiedy ostatnio wszedl i ile wskazan ocenil.
 *
 * Cztery stany (P-6): ladowanie, blad z ponowieniem, pusto (z zacheta), dane. Przy danych wiersz ma dwie akcje, ale tylko
 * dla linku aktywnego: wygasly i uniewazniony nie maja czego kopiowac ani uniewazniac.
 *
 * Kopiowanie bierze PELNY adres (origin przegladarki plus sciezka z serwera). Uniewaznienie idzie przez ConfirmDialog (D-4):
 * to akcja, ktorej nie da sie cofnac z poziomu interfejsu.
 */

export function KartaLinkow({
  zasob,
  onNowyLink,
}: {
  zasob: StanZasobu<LinkRecenzjiAdmin[]>;
  onNowyLink: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const { skopiowano, kopiuj } = useKopiowanie();
  const [doUniewaznienia, setDoUniewaznienia] = useState<LinkRecenzjiAdmin | null>(null);
  const [uniewaznianie, setUniewaznianie] = useState(false);
  const { dane, blad, odswiez } = zasob;
  const teraz = new Date();
  const refListy = useRef<HTMLUListElement>(null);
  const refNowyLink = useRef<HTMLButtonElement>(null);
  /** Id linku, ktory wlasnie uniewaznilismy: po odswiezeniu listy jego wiersz traci przyciski, a z nimi fokus. */
  const poUniewaznieniu = useRef<string | null>(null);

  // Uniewazniony wiersz traci "Kopiuj link" i "Uniewaznij", a okno potwierdzenia oddalo fokus wlasnie tym przyciskom, wiec
  // po odswiezeniu listy fokus laduje na <body>. Przenosimy go na przycisk w nastepnym wierszu (albo poprzednim), a gdy
  // zadnego nie ma, na "Nowy link". Czekamy na liste PO uniewaznieniu (stara jeszcze ma link jako aktywny), a gdy
  // uzytkownik zdazyl pojsc gdzie indziej, niczego mu nie zabieramy.
  useEffect(() => {
    const id = poUniewaznieniu.current;
    if (id === null || dane === null) return;
    if (dane.find((l) => l.id === id)?.status === "AKTYWNY") return;
    poUniewaznieniu.current = null;
    if (!czyFokusZginal()) return;
    const wiersze = Array.from(refListy.current?.querySelectorAll("li") ?? []);
    const pozycja = wiersze.findIndex((w) => w.getAttribute("data-link-id") === id);
    (najblizszyPrzycisk(wiersze, pozycja + 1) ?? refNowyLink.current)?.focus();
  }, [dane]);

  async function kopiujLink(link: LinkRecenzjiAdmin): Promise<void> {
    const udalo = await kopiuj(link.id, pelnyAdresLinku(link, window.location.origin));
    // Blad, nie ostrzezenie: tak samo jak w oknie nowego linku (DialogLinkuRecenzji), zeby jeden panel nie mial dwoch
    // rodzajow tego samego komunikatu.
    if (!udalo) toast.blad("Nie udało się skopiować do schowka. Spróbuj jeszcze raz albo otwórz okno nowego linku.");
  }

  async function uniewaznij(): Promise<void> {
    if (!doUniewaznienia || uniewaznianie) return;
    setUniewaznianie(true);
    try {
      await api.recenzjeUniewaznijLink(doUniewaznienia.id);
      poUniewaznieniu.current = doUniewaznienia.id;
      toast.sukces(`Link dla: ${doUniewaznienia.etykieta} został unieważniony.`);
      setDoUniewaznienia(null);
      odswiez();
    } catch (err) {
      toast.blad(komunikatBleduRecenzji(err instanceof ApiError ? err.code : "siec"));
    } finally {
      setUniewaznianie(false);
    }
  }

  return (
    <Card
      tytul="Linki"
      akcje={
        <Button ref={refNowyLink} wariant="primary" onClick={onNowyLink}>
          Nowy link
        </Button>
      }
    >
      {dane === null && blad !== null ? (
        <StanBledu
          tytul="Nie udało się wczytać linków"
          opis={komunikatBleduRecenzji(blad)}
          akcja={
            <Button wariant="secondary" onClick={odswiez}>
              Spróbuj ponownie
            </Button>
          }
        />
      ) : dane === null ? (
        <StanLadowania tekst="Wczytuję linki..." />
      ) : dane.length === 0 ? (
        <StanPusty
          tytul="Nie ma jeszcze żadnego linku"
          opis="Utwórz link, żeby ekspert mógł przejrzeć wybrane rozmowy i zostawić uwagi bez logowania."
        />
      ) : (
        <ul className="rowlist" ref={refListy}>
          {dane.map((link) => {
            const postep = postepLinku(link.postep);
            const aktywny = link.status === "AKTYWNY";
            return (
              <li className="rowlist__row recenzje-wiersz" data-status={link.status} data-link-id={link.id} key={link.id}>
                <Avatar nazwa={link.etykieta} rozmiar="m" />
                <div className="rowlist__body">
                  <p className="rowlist__nazwa">
                    <span className="recenzje-wiersz__etykieta">{link.etykieta}</span>
                    <Badge wariant={WARIANTY_STATUSU_LINKU[link.status]} kropka>
                      {ETYKIETY_STATUSU_LINKU[link.status]}
                    </Badge>
                  </p>
                  <p className="rowlist__meta">{metaLinku(link, teraz)}</p>
                  {postep && (
                    <div className="rowlist__postep">
                      <ProgressBar etykieta={postep.etykieta} procent={postep.procent} />
                    </div>
                  )}
                </div>
                {aktywny && (
                  <div className="rowlist__actions">
                    <Button
                      wariant="secondary"
                      aria-label={`${skopiowano === link.id ? "Skopiowano link" : "Kopiuj link"}: ${link.etykieta}`}
                      onClick={() => void kopiujLink(link)}
                    >
                      <IconKopiuj size={18} />
                      {skopiowano === link.id ? "Skopiowano" : "Kopiuj link"}
                    </Button>
                    <Button
                      wariant="danger"
                      aria-label={`Unieważnij link: ${link.etykieta}`}
                      onClick={() => setDoUniewaznienia(link)}
                    >
                      Unieważnij
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Montowane dopiero po kliknieciu "Uniewaznij": tytul zawsze niesie etykiete wybranego linku. */}
      {doUniewaznienia && (
        <ConfirmDialog
          otwarty
          tytul={`Unieważnić link dla: ${doUniewaznienia.etykieta}?`}
          tresc="Osoba z tym linkiem od razu straci dostęp do rozmów. Uwagi, które już zostawiła, zostają w panelu Poprawki."
          etykietaPotwierdzenia="Unieważnij link"
          zajety={uniewaznianie}
          onPotwierdz={() => void uniewaznij()}
          onAnuluj={() => {
            if (!uniewaznianie) setDoUniewaznienia(null);
          }}
        />
      )}
    </Card>
  );
}
